// noinspection DuplicatedCode,JSUnusedGlobalSymbols

import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { fromEvent } from 'rxjs';
import { TableElement, TableExportUtil, UnsubscribeOnDestroyAdapter } from '@shared';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRippleModule } from '@angular/material/core';
import { FeatherIconsComponent } from '@shared/components/feather-icons/feather-icons.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { BusinessGroupService } from '@core/service/business-group.service';
import { BranchService } from '@core/http/table-data-services/Branch.service';
import { Branch } from '@core/models/Branch';
import { DoDeleteComponent } from '@shared/components/do-delete/do-delete.component';
import { Router } from '@angular/router';
import { GeneralFunctionsService } from '@core/service/generalFunctions.service';
import { BranchDataSource } from '@core/data-source/BranchDataSource';
import { TopWidgetsComponent } from '@shared/components/top-widgets/top-widgets.component';
import { HttpMesageHandlerService } from '@core/service/http-mesage-handler';
import { AuthManagementService } from '@core/service/auth-management.service';
import { NgClass, NgIf } from '@angular/common';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-branch-list',
  standalone: true,
  imports: [
    BreadcrumbComponent,
    MatTooltipModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatSortModule,
    MatCheckboxModule,
    FeatherIconsComponent,
    MatRippleModule,
    MatProgressSpinnerModule,
    MatPaginatorModule,
    TopWidgetsComponent,
    NgIf,
    NgClass,
  ],
  templateUrl: './branch-list.component.html',
  styleUrl: './branch-list.component.scss',
})
export class BranchListComponent extends UnsubscribeOnDestroyAdapter implements OnInit {
  displayedColumns: string[] = [];
  data!: BranchService;
  dataSource!: BranchDataSource;
  index?: number;
  idSucursal?: string;
  branch?: Branch;

  isAdmin = false;
  isParticular = false;
  businessGroupId: string | null = null;

  @ViewChild(MatPaginator, { static: true })
  paginator!: MatPaginator;

  @ViewChild(MatSort, { static: true })
  sort!: MatSort;

  @ViewChild('filter', { static: true })
  filter?: ElementRef;

  constructor(
    public httpClient: HttpClient,
    public dialog: MatDialog,
    private router: Router,
    private gfs: GeneralFunctionsService,
    public branchService: BranchService,
    private businessGroupService: BusinessGroupService,
    private httpMsgHandler: HttpMesageHandlerService,
    private authManagement: AuthManagementService
  ) {
    super();
  }

  ngOnInit(): void {
    this.isAdmin = this.authManagement.isUserAdmin();
    this.isParticular = this.authManagement.isUserParticular();

    this.configureDisplayedColumns();
    this.registerFilterListener();

    this.businessGroupId =
      this.businessGroupService.currentId ||
      localStorage.getItem('businessGroupId');

    if (this.isParticular) {
      this.loadData();
    }

    this.subs.sink = this.businessGroupService.businessGroupId$.subscribe((id) => {
      this.businessGroupId = id || localStorage.getItem('businessGroupId');

      if (this.isParticular) {
        return;
      }

      if (!this.businessGroupId) {
        return;
      }

      this.loadData();
    });
  }

  refresh(): void {
    this.loadData();
  }

  addNew(): void {
    const gemp = this.businessGroupId || localStorage.getItem('businessGroupId');
    if (!gemp) return;

    const businessGroupId = btoa(gemp);
    this.router.navigate(['branch/create-edit/' + businessGroupId]).then();
  }

  editCall(row: Branch): void {
    const gemp = this.businessGroupId || localStorage.getItem('businessGroupId');
    if (!gemp || !row?.idSucursal) return;

    const businessGroupId = btoa(gemp);
    const idSucursal = btoa(row.idSucursal);
    this.router.navigate(['branch/create-edit/' + businessGroupId + '/' + idSucursal]).then();
  }

  deleteItem(row: Branch): void {
    if (!this.isAdmin) {
      return;
    }

    this.idSucursal = row.idSucursal;

    const dialogRef = this.dialog.open(DoDeleteComponent, {
      data: {
        toDelete: 'Sucursal',
        id: this.idSucursal,
        name: row.nombre,
        actionService: (id: string) => this.data.deleteBranch(id),
      }
    });

    this.subs.sink = dialogRef.afterClosed().subscribe((result) => {
      if (result === 1) {
        this.reloadFromBackend();
        this.paginator._changePageSize(this.paginator.pageSize);
      }
    });
  }

  goToProfile(row: Branch): void {
    const gemp = this.businessGroupId || localStorage.getItem('businessGroupId');
    if (!gemp || !row?.idSucursal) return;

    const businessGroupId = btoa(gemp);
    const idSucursal = btoa(row.idSucursal);
    this.router.navigate(['branch/profile/' + businessGroupId + '/' + idSucursal]).then();
  }

  public loadData(): void {
    this.data = this.branchService;

    this.data.clearBranches();

    this.data.isTblLoading = true;

    this.dataSource = new BranchDataSource(
      this.businessGroupId,
      this.data,
      this.paginator,
      this.sort,
      false
    );

    if (this.filter?.nativeElement?.value) {
      this.dataSource.filter = this.filter.nativeElement.value;
    }

    // la carga se hace aquí, no en connect()
    if (this.isParticular) {
      this.data.getAllBranches(undefined, false);
    } else {
      this.data.getAllBranches(this.businessGroupId ?? undefined, false);
    }
  }

  exportExcel(): void {
    const exportData: Partial<TableElement>[] = this.dataSource.filteredData.map((x) => ({
      Nombre: x.nombre,
      Registro_Sanitario: x.registroSanitario,
      Status: x.status,
      Direccion: x.domicilio,
      Responsable: x.responsable,
      Cedula_Responable: x.cedulaResponsable,
      Telefono_Responsable: x.telefonoResponsable,
      Correo: x.emailResponsable,
    }));

    TableExportUtil.exportToExcel(exportData, 'excel');
  }

  activateBranch(row: Branch): void {
    if (!this.isAdmin) return;
    if (!row?.idSucursal) return;

    Swal.fire({
      icon: 'question',
      title: 'ACTIVAR SUCURSAL',
      text: `¿DESEAS ACTIVAR LA SUCURSAL "${row.nombre}"?`,
      showCancelButton: true,
      confirmButtonText: 'SI, ACTIVAR',
      cancelButtonText: 'CANCELAR',
    }).then((r) => {
      if (!r.isConfirmed) return;

      this.branchService.getByIdSucursal(row.idSucursal).subscribe({
        next: (res: any) => {
          const b: Branch = res.data;

          const payload: any = {
            ...b,
            status: 'Activo',
          };

          payload.nombre = (payload.nombre || '').toString().toUpperCase();
          payload.domicilio = (payload.domicilio || '').toString().toUpperCase();
          payload.responsable = (payload.responsable || '').toString().toUpperCase();

          this.branchService.createUpdateBranch(payload).subscribe({
            next: () => {
              Swal.fire({
                icon: 'success',
                title: 'SUCURSAL ACTIVADA',
                text: 'LA SUCURSAL SE ACTIVO CORRECTAMENTE.',
              }).then();

              this.reloadFromBackend();
              this.paginator._changePageSize(this.paginator.pageSize);
            },
            error: (err: any) => {
              this.httpMsgHandler.handleMsg(err);
            },
          });
        },
        error: (err: any) => {
          this.httpMsgHandler.handleMsg(err);
        },
      });
    });
  }

  private configureDisplayedColumns(): void {
    if (this.isParticular) {
      this.displayedColumns = ['nombre', 'direccion', 'acciones'];
    } else {
      this.displayedColumns = ['nombre', 'direccion', 'responsable', 'correo', 'acciones'];
    }
  }

  private reloadFromBackend(): void {
    if (!this.data) return;

    if (this.isParticular) {
      this.data.getAllBranches(undefined, false);
      return;
    }

    const gemp = this.businessGroupId || localStorage.getItem('businessGroupId');
    if (gemp) {
      this.data.getAllBranches(gemp, false);
    }
  }

  private registerFilterListener(): void {
    if (!this.filter?.nativeElement) return;

    this.subs.sink = fromEvent(this.filter.nativeElement, 'keyup').subscribe(() => {
      if (!this.dataSource) return;
      this.dataSource.filter = this.filter?.nativeElement.value ?? '';
    });
  }
}
